import { useGetTSPsQuery } from '../services/edgeApi';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Workbook, { Matrix } from 'react-spreadsheet';
import { CellBase } from 'react-spreadsheet';
import { useTheme } from '../helpers/hooks';
import { formulaParsers } from '../helpers/Spreadsheet';
import { isValidNumber } from '../utils/numberUtils';
import { Rate, Zone } from '@/types';
import { RatesPatch } from '@/types/api/RateSchedule';
import { useForceUpdate } from '../hooks/useForceUpdate';

const formatZones = (rawZones: Zone[]) =>
  rawZones.map((zone) => [Number(zone.id), zone.name] as [number, string]);

const fillCells = (rates: Rate[], zones: [number, string][]) => {
  if (!rates || !zones) {
    return [] as Matrix<CellBase>;
  }
  return zones.reduce<Matrix<CellBase>>((acc, [rowZoneId]) => {
    const row: CellBase[] = [];
    zones.forEach(([colZoneId]) => {
      const rate = rates.find(
        (r) => r.receipt_zone === rowZoneId && r.delivery_zone === colZoneId,
      );
      row.push({
        value: rate ? rate.value.toString() : null,
        className: 'text-start px-2',
      });
    });
    acc.push(row);
    return acc;
  }, []);
};

const getFormattedData = (raw: Matrix<CellBase>) => {
  return raw.map((row) =>
    row.map((cell) =>
      cell?.value === undefined || cell?.value === null ? null : cell.value,
    ),
  );
};

export type RatesTableProps = {
  style?: React.CSSProperties;
  rateSchedule: string;
  rates: Rate[];
  zones: Record<string, Zone> | undefined;
  onChange?: (values: Matrix<CellBase>) => void;
};

export type RatesTableRef = {
  getPatch: () => RatesPatch;
  getData: () => (string | null)[][];
};

export const _RatesTable = (
  { style, rateSchedule, rates, zones, onChange }: RatesTableProps,
  ref: React.Ref<RatesTableRef>,
) => {
  useGetTSPsQuery(undefined);

  const zoneRates = useMemo(() => {
    return rates.filter(
      (rate) =>
        rate.receipt_zone &&
        rate.delivery_zone &&
        rate.receipt_points.length === 0 &&
        rate.delivery_points.length === 0,
    );
  }, [rates, rateSchedule]);

  const [theme] = useTheme();
  const forceUpdate = useForceUpdate();
  const [dragRow, setDragRow] = useState<string | null>(null);
  const [orderedZones, setOrderedZones] = useState<[number, string][]>(
    formatZones(Object.values(zones ?? {})),
  );

  useEffect(() => {
    setOrderedZones(formatZones(Object.values(zones ?? {})));
  }, [zones]);

  const data = useRef<Matrix<CellBase>>(fillCells(zoneRates, orderedZones));

  useEffect(() => {
    data.current = fillCells(zoneRates, orderedZones);
    setTimeout(forceUpdate, 50);
  }, [zoneRates, orderedZones]);

  useImperativeHandle(
    ref,
    () => {
      return {
        getData() {
          return getFormattedData(data.current);
        },
        getPatch() {
          const updateRates: { id: number; value: number }[] = [];
          const createRates: {
            value: number;
            receipt_zone: number;
            delivery_zone: number;
          }[] = [];
          getFormattedData(data.current).forEach((dataRow, rowIndex) => {
            dataRow.forEach((value, colIndex) => {
              if (!value) {
                return;
              }
              const receiptZoneId = orderedZones[rowIndex][0];
              const deliveryZoneId = orderedZones[colIndex][0];
              const originalRate = zoneRates.find(
                (r) =>
                  r.receipt_zone === receiptZoneId &&
                  r.delivery_zone === deliveryZoneId,
              );
              if (
                originalRate &&
                Number(originalRate.value) === Number(value)
              ) {
                return;
              }
              originalRate
                ? updateRates.push({
                    id: originalRate.id,
                    value: isValidNumber(value) ? Number(value) : 0,
                  })
                : createRates.push({
                    value: isValidNumber(value) ? Number(value) : 0,
                    receipt_zone: receiptZoneId,
                    delivery_zone: deliveryZoneId,
                  });
            });
          });
          return {
            rate_schedule_id: Number(rateSchedule),
            rates: { update: updateRates, create: createRates },
          };
        },
      };
    },
    [data, rateSchedule, zoneRates, orderedZones],
  );

  const zoneNames = useMemo(() => {
    return orderedZones.map(([_, zoneName]) => zoneName);
  }, [orderedZones]);

  const switchRows = (fromZone: string, toZone: string) => {
    const fromIndex = zoneNames.indexOf(fromZone);
    const toIndex = zoneNames.indexOf(toZone);
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }
    const newCells = [...data.current];
    const [fromRow] = newCells.splice(fromIndex, 1);
    newCells.splice(toIndex, 0, fromRow);
    newCells.forEach((row) => {
      const [fromCell] = row.splice(fromIndex, 1);
      row.splice(toIndex, 0, fromCell);
    });
    data.current = newCells;

    const newOrderedZones = [...orderedZones];
    const [movedZone] = newOrderedZones.splice(fromIndex, 1);
    newOrderedZones.splice(toIndex, 0, movedZone);
    setOrderedZones(newOrderedZones);
  };

  return (
    <div style={style}>
      <Workbook
        darkMode={theme === 'dark'}
        Table={(props) => (
          <table {...props}>
            <tbody>{props.children}</tbody>
          </table>
        )}
        data={data.current}
        onChange={(values) => {
          data.current = values;
          onChange?.(values);
        }}
        createFormulaParser={(d) => formulaParsers(d, undefined)}
        rowLabels={zoneNames}
        columnLabels={zoneNames}
        RowIndicator={(props) => (
          <th
            style={{
              cursor: 'grab',
            }}
            className={`Spreadsheet__header ${
              props.selected ? 'Spreadsheet__header--selected' : ''
            }`}
            onClick={() => props.onSelect(props.row, true)}
            draggable="true"
            onDragStart={() => {
              setDragRow(zoneNames[props.row]);
            }}
            onDragEnd={() => {
              setDragRow(null);
            }}
            onDrop={() => {
              const row = zoneNames[props.row];
              if (dragRow) {
                switchRows(dragRow, row);
              }
            }}
            onDragOver={(dragOverEvt) => {
              dragOverEvt.preventDefault();
            }}
          >
            {props.label}
          </th>
        )}
      />
    </div>
  );
};

export const RatesTable = forwardRef(_RatesTable);
